import {ElMessage} from 'element-plus';
import {getReasonPhrase} from 'http-status-codes';
import {ClassConstructor, plainToInstance} from 'class-transformer';

import {AvailabilityState, CabMe, CabObj, CabObjCreate, CabPeriod, CabReportItem, CabUser, CrudAction, deserializeWithDeduplication, JSONstringify, Selector, StatusEnum, TRACE_CALL_BACKEND} from 'common';

import {getBackendUrl} from './frontendConfig.js';
import {debounce} from './debouncer.js';
import {ReportGroup} from './utils.js';

const TRACE: boolean        = TRACE_CALL_BACKEND;
const TRACE_STACKS: boolean = false;

const METHOD_GET: string    = 'GET';
const METHOD_POST: string   = 'POST';
const METHOD_DELETE: string = 'DELETE';
const METHOD_PATCH: string  = 'PATCH';

//////////////////////////////////////////////////////////////////////////////////////////
///////////////////////////////generic object crud////////////////////////////////////////
//////////////////////////////////////////////////////////////////////////////////////////

/**
 * Generic list endpoint caller for Cab* objects.
 * @param type The class constructor for the Cab* type (e.g., CabKPI)
 * @param selector The filter object for the query
 * @returns An array of instances of the requested type
 */
export async function listObjects<T extends CabObj>(type: ClassConstructor<T>, selector?: Selector): Promise<T[]> {
    const endpoint: string = `/api/obj/${type.name.substring(3).toLocaleLowerCase()}`;
    const rawList: unknown = await backend(METHOD_POST, endpoint, selector);
    return plainToInstance(type, rawList as T[]);
}

/**
 * Generic read endpoint caller for Cab* objects.
 * @param obj The object to read
 * @returns obj refreshed with all the stored properties from the backend
 */
export async function readObject<C extends CabObjCreate<T>, T extends CabObj>(obj: T): Promise<T> {
    const endpoint: string = `/api/obj/${obj.constructor.name.substring(3).toLocaleLowerCase()}/read`;
    const rawObj           = await backend(METHOD_POST, endpoint, obj) as T;
    return plainToInstance(obj.constructor as C, rawObj);
}

/**
 * Helper to create a Cab* object via the generic writeObject function.
 * @param obj The object to create
 * @param throwError
 * @returns wethert he operation was succesful
 */
export async function createObject<T extends CabObj>(obj: T, throwError?: boolean): Promise<boolean> {
    return writeObject(CrudAction.create, obj, throwError);
}

/**
 * Helper to update a Cab* object via the generic writeObject function.
 * @param obj The object to update
 * @param throwError
 * @returns wethert he operation was succesful
 */
export async function updateObject<T extends CabObj>(obj: T, throwError?: boolean): Promise<boolean> {
    return writeObject(CrudAction.update, obj, throwError);
}

/**
 * Helper to delete a Cab* object via the generic writeObject function.
 * @param obj The object to delete
 * @param throwError
 * @returns wethert he operation was succesful
 */
export async function deleteObject<T extends CabObj>(obj: T, throwError?: boolean): Promise<boolean> {
    return writeObject(CrudAction.delete, obj, throwError);
}

/**
 * Generic create/update/delete endpoint caller for Cab* objects.
 * @param action The CRUD action (CrudAction.create, CrudAction.update, CrudAction.delete)
 * @param obj The object to send
 * @param throwError
 * @returns whether he operation was successful
 */
async function writeObject<T extends CabObj>(action: Exclude<CrudAction, CrudAction.list>, obj: T, throwError?: boolean): Promise<boolean> {
    const endpoint = `/api/obj/${obj.constructor.name.substring(3).toLocaleLowerCase()}/${action}`;
    let method: string;

    switch (action) {
        case CrudAction.create:
            method = METHOD_POST;
            break;
        case CrudAction.update:
            method = METHOD_PATCH;
            break;
        case CrudAction.delete:
            method = METHOD_DELETE;
            break;
        default:
            throw new Error(`Unsupported CrudAction for crudObject: ${action}`);
    }

    let response: Response;
    if (action === CrudAction.update) {
        response = await debounce(obj.uuid, async () => await backendRaw(method, endpoint, JSONstringify(obj), 'application/json'));
    } else {
        response = await backendRaw(method, endpoint, JSONstringify(obj), 'application/json');
    }
    if (!response.ok) {
        if (throwError) {
            throw new BackendError(`Backend call did not succeed: ${method} ${endpoint} (${response.status})`, JSONstringify(obj));
        } else {
            ElMessage.error(`Error: server responed with ${response.status} ${getReasonPhrase(response.status)}`);
            return false;
        }
    }
    return true;
}

export async function syncUsers() {
    return await backend(METHOD_GET, `/api/obj/user/sync`, undefined);
}

export async function getMessages(): Promise<Record<string, string[]>> {
    return await backend(METHOD_GET, `/api/admin/messages`, undefined);
}

export async function clearMessages() {
    await backend(METHOD_POST, `/api/admin/messages/clear`, undefined);
}

export async function setAvailabilityForReporters(state: AvailabilityState) {
    return await backend(METHOD_POST, `/api/admin/state-for-reporters`, {state});
}

export async function getDatabaseVersionFromDb() {
    return await backend(METHOD_GET, `/api/admin/databaseVersion`, undefined);
}

export async function getCacheStats() {
    return await backend(METHOD_GET, `/api/admin/cache-stats`, undefined);
}

export async function devEraseData() {
    return await backend(METHOD_GET, `/api/admin/erase-data`, undefined);
}

// noinspection JSUnusedGlobalSymbols
export async function devCurrentDate(d: Date) {
    return await backend(METHOD_POST, `/api/admin/current-date`, {date: d});
}

export async function expressieDoc(): Promise<string> {
    return await backend(METHOD_GET, `/api/admin/expressiedoc`, undefined);
}

// noinspection JSUnusedGlobalSymbols
export async function backup_db(): Promise<Response> {
    return await backendRaw(METHOD_GET, `/api/admin/backup_db`);
}

// noinspection JSUnusedGlobalSymbols
export async function backup_db_legacy(): Promise<Response> {
    return await backendRaw(METHOD_GET, `/api/admin/backup_db_legacy`);
}

export async function restore(body: any): Promise<Response> {
    return await backendRaw(METHOD_POST, `/api/admin/restore`, body);
}

export async function verifyDatabase(): Promise<any> {
    return await backend(METHOD_GET, `/api/admin/verifyDatabase`, undefined);
}

export async function backup(): Promise<Response> {
    return await backendRaw(METHOD_GET, `/api/admin/backup`);
}

export async function expressionDump(): Promise<any> {
    return await backendRaw(METHOD_GET, `/api/admin/expressiondump`);
}

export async function tempPassword(email: string): Promise<string> {
    const response: any = await backend(METHOD_GET, `/api/admin/temp-password?email=${encodeURIComponent(email)}`);
    return response?.pw;
}

export async function exportReportItemsForAdmin(): Promise<Response> {
    return await backendRaw(METHOD_GET, `/api/admin/exportReportItems`);
}

export async function exportReportItemsForAdminCsv(): Promise<Response> {
    return await backendRaw(METHOD_GET, `/api/admin/exportReportItemsCsv`);
}

export async function exportKpiMilestonesForAdmin(): Promise<Response> {
    return await backendRaw(METHOD_GET, `/api/admin/exportKpiMilestones`);
}

export async function tnComplianceCsvExport(period: CabPeriod): Promise<Response> {
    return await backendRaw(METHOD_POST, `/api/admin/tnComplianceCsvExport`, JSONstringify({period}), 'application/json');
}

export async function exportDocumentaryProofOverview(period: CabPeriod) {
    return backendRaw(METHOD_GET, `/api/admin/exportDocumentaryProofOverview?period=${encodeURIComponent(period.toString())}`);
}

export async function exportDocumentaryProofOverviewZip(period: CabPeriod) {
    return backendRaw(METHOD_GET, `/api/admin/exportDocumentaryProofOverviewZip?period=${encodeURIComponent(period.toString())}`);
}

//////////////////////////////////////////////////////////////////////////////////////////
/////////////////////////////////////report///////////////////////////////////////////////
//////////////////////////////////////////////////////////////////////////////////////////

export async function listReporterReportItems(group: ReportGroup): Promise<CabReportItem[]> {
    let path: string;
    switch (group) {
        case ReportGroup.verleden:
            path = `/api/report/reportitem/verleden`;
            break;
        case ReportGroup.heden:
            path = `/api/report/reportitem/heden`;
            break;
        case ReportGroup.toekomst:
            path = `/api/report/reportitem/toekomst`;
    }
    return plainToInstance(CabReportItem, await backend(METHOD_POST, path));
}

export async function listAllReporterReportItems(statusFilter?: StatusEnum[]): Promise<CabReportItem[]> {
    const body = statusFilter && statusFilter.length > 0 ? {status: statusFilter} : undefined;
    return plainToInstance(CabReportItem, await backend(METHOD_POST, `/api/report/reportitem/all`, body));
}

//TODO: replace by generic call(?)
export async function updateReportItem(item: CabReportItem) {
    const result = await debounce(item.uuid,
        async () => await backend(METHOD_PATCH, `/api/report/reportitem/update`, item),
    );
    if (result.update) {
        return plainToInstance(CabReportItem, result.update as CabReportItem);
    }
    console.log(`Could not update: ${result.message}`);
    return undefined;
}

export async function updateReportItemAsAdmin(item: CabReportItem) {
    const result = await debounce(item.uuid,
        async () => await backend(METHOD_PATCH, `/api/admin/reportitem/update`, item),
    );
    if (result.update) {
        return plainToInstance(CabReportItem, result.update as CabReportItem);
    }
    console.log(`Could not update: ${result.message}`);
    return undefined;
}

export async function exportReportItemsForReporter(): Promise<Response> {
    return await backendRaw(METHOD_GET, `/api/report/exportReportItems`);
}

//////////////////////////////////////////////////////////////////////////////////////////
//////////////////////////////documentary proof///////////////////////////////////////////
//////////////////////////////////////////////////////////////////////////////////////////

export async function getDocumentaryProof(uuid: string): Promise<Response> {
    return await backendRaw(METHOD_GET, `/api/documentaryProof?uuid=${uuid}`);
}

export async function updateDocumentaryProof(uuid: string, data: FormData): Promise<Response> {
    return await backendRaw(METHOD_POST, `/api/documentaryProof?uuid=${uuid}`, data);
}

export async function deleteDocumentaryProof(uuid: string) {
    await backend(METHOD_DELETE, `/api/documentaryProof?uuid=${uuid}`);
}

//////////////////////////////////////////////////////////////////////////////////////////
/////////////////////////////////////misc///////////////////////////////////////////////////
//////////////////////////////////////////////////////////////////////////////////////////

// noinspection JSUnusedGlobalSymbols
export async function getMe(): Promise<CabMe> {
    return await backend(METHOD_GET, `/api/me`);
}

export async function getCurrentUser(): Promise<CabUser> {
    return await backend(METHOD_GET, `/api/currentuser`);
}

export async function getStateForReporters(): Promise<AvailabilityState> {
    try {
        const result = await backend(METHOD_GET, `/api/reportState`, undefined);
        return result.state;
    } catch (e) {
        return AvailabilityState.closed;
    }
}

//////////////////////////////////////////////////////////////////////////////////////////
/////////////////////////////////////accept///////////////////////////////////////////////
//////////////////////////////////////////////////////////////////////////////////////////

// noinspection JSUnusedGlobalSymbols
export async function changeAcceptance(uuid: string, status: StatusEnum): Promise<CabReportItem | undefined> {
    const result = await backend(METHOD_POST, `/api/accept/changeAcceptance`, {
        uuid  : uuid,
        status: status,
    });
    if (result.update) {
        return plainToInstance(CabReportItem, result.update as CabReportItem);
    } else {
        throw new BackendError(`Failed to change acceptance status`, {
            uuid,
            status,
            message: result.message,
        });
    }
}

///////////////////////////////////////////////////////////////////////////////////////////////////////
////////////////////////////////////////////helper functions///////////////////////////////////////////
///////////////////////////////////////////////////////////////////////////////////////////////////////

async function backend(method: string, path: string, body?: any): Promise<any> {  //TODO: body non-optional?
    const response: Response = await backendRaw(method, path, body ? JSONstringify(body) : undefined, body ? 'application/json' : undefined);
    const txt: string        = await response.text();

    if (!response.ok) {
        throw new BackendError(`Backend call did not succeed: ${method} ${path} (${response.status})`, txt);
    }

    let parsed;
    try {
        parsed = deserializeWithDeduplication(txt);
    } catch (_) {
        parsed = undefined;
    }

    if (!parsed) {
        console.log(`+++++++ ${txt} +++++++`);
        throw new BackendError(`Failed to parse JSON response`, {rawResponse: txt});
    } else {
        return parsed;
    }
}

async function backendRaw(method: string, path: string, body?: any, contentType?: string, params?: Record<string, string>): Promise<Response> {
    if (!path.startsWith(`/`)) {
        throw new Error(`backend calls should start with '/': ${method} ${path}`);
    }
    const headers: [string, string][] = [];
    const init: RequestInit           = {
        method : method,
        headers: headers,
    };
    if (body) {
        init.body = body;
    }
    if (contentType) {
        headers.push(['Content-Type', contentType]);
    }
    const url = getBackendUrl();
    if (params) {
        const queryString = new URLSearchParams(params).toString();
        path += `?${queryString}`;
    }
    if (TRACE) {
        if (TRACE_STACKS) {
            console.trace(`BACKEND CALL: >>>>> ${method.padEnd(5)} ${path}`);
        } else {
            console.info(`BACKEND CALL: >>>>> ${method.padEnd(5)} ${path}`);
        }
    }
    const response = await fetch(`${url}${path}`, init);
    if (TRACE) {
        console.info(`BACKEND CALL: ${method.padEnd(5)} <<<<< ${path}\n\n`, response);
    }
    return response;
}

export class BackendError extends Error {
    constructor(message: string, public details: any) {
        super(message);
        this.name = 'BackendError';
    }

    override toString(): string {
        return `${this.message} (${this.details})`;
    }
}
